import React from 'react';
import { Box, Card, CardContent, Grid, Rating, Typography, Chip } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { CustomButton } from '../../../../../components';
import { APP_ROUTES } from '../../../../../constants/app-routes';
import { APPLICATION_STATUS } from '../../../../../constants/event-application-status';
import { updateRetailApplicationStatus } from '../../../../../core/vendor-retail/action';
import { useIsMobile, useStorageImage } from '../../../../../hooks';

function CardActions({ vendor, handleApprove, handleDeny, isMobile }) {
  return (
    <Grid item xs={12} sm={9} px={isMobile ? 0 : 4}>
      <Box display="flex">
        <Typography variant="body1" fontWeight="bold" pr={isMobile ? 3 : 0}>
          {isMobile ? 'Rating:' : 'fayVen Community Rating:'}
        </Typography>
        <Rating value={vendor.averageScore || 0} disabled />
      </Box>
      <Box display="flex" mt={2} columnGap={2} justifyContent="space-between">
        <CustomButton variant="contained" onClick={handleApprove}>
          Approve
        </CustomButton>
        <CustomButton variant="outlined" onClick={handleDeny}>
          Deny
        </CustomButton>
      </Box>
    </Grid>
  );
}

CardActions.propTypes = {
  vendor: PropTypes.instanceOf(Object).isRequired,
  handleApprove: PropTypes.func.isRequired,
  handleDeny: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

function RetailApplicationCard({ application, onStatusChanged }) {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { vendor, retail_schedule: schedule } = application;

  const changeStatus = (newStatus) => {
    dispatch(updateRetailApplicationStatus(application.id, newStatus, onStatusChanged));
  };

  const handleApprove = () => changeStatus(APPLICATION_STATUS.APPROVED);
  const handleDeny = () => changeStatus(APPLICATION_STATUS.DENIED);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'incomplete':
        return 'default';
      case 'approved':
        return 'success';
      case 'denied':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Grid container mb={2}>
      <Grid item xs={12} sm={3}>
        <Card>
          <CardContent>
            <Typography variant="body1" fontWeight="bold">
              <Link to={APP_ROUTES.VENDOR_PUBLIC_PROFILE.replace(':vendorId', vendor?.id)}>
                {vendor?.name || 'Unknown Vendor'}
              </Link>
            </Typography>

            <Box mt={1} mb={1}>
              <Chip
                label={application.application_status}
                color={getStatusColor(application.application_status)}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              <strong>Date:</strong> {moment(application.booking_date).format('LL')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Time:</strong>{' '}
              {moment(application.start_time, ['HH:mm', 'HH:mm:ss']).format('h:mm A')}
              {' - '}
              {moment(application.end_time, ['HH:mm', 'HH:mm:ss']).format('h:mm A')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Duration:</strong> {application.duration_hours} hours
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Total:</strong> ${application.total_price}
            </Typography>

            {application.contact_name && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                <strong>Contact:</strong> {application.contact_name}
              </Typography>
            )}
            {application.contact_phone && (
              <Typography variant="body2" color="text.secondary">
                {application.contact_phone}
              </Typography>
            )}
            {application.contact_email && (
              <Typography variant="body2" color="text.secondary">
                {application.contact_email}
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Est. {new Date(vendor?.created_at).getFullYear()}
            </Typography>

            {isMobile && (
              <CardActions
                vendor={vendor}
                handleApprove={handleApprove}
                handleDeny={handleDeny}
                isMobile={isMobile}
              />
            )}
          </CardContent>
        </Card>
      </Grid>
      {!isMobile && (
        <CardActions
          vendor={vendor}
          handleApprove={handleApprove}
          handleDeny={handleDeny}
          isMobile={isMobile}
        />
      )}
    </Grid>
  );
}

RetailApplicationCard.propTypes = {
  application: PropTypes.instanceOf(Object).isRequired,
  onStatusChanged: PropTypes.func,
};

RetailApplicationCard.defaultProps = {
  onStatusChanged: null,
};

export default RetailApplicationCard;
